
import React from "react";

export class FoodSource {
    static ENUM_VALUES: FoodSource[] = [
        new FoodSource(0, "No Source", "https://www.nutricherry.com/"),
        new FoodSource(1, "USDA Legacy", "https://fdc.nal.usda.gov/"),
        new FoodSource(2, "USDA Foundation", "https://fdc.nal.usda.gov/"),
        new FoodSource(3, "USDA Commercial", "https://fdc.nal.usda.gov/"),
        new FoodSource(4, "NutriCherry Recipe", "https://www.nutricherry.com/"),
    ];

    id: number;
    name: string;
    website: string;

    constructor(id: number, name: string, website: string) {
        this.id = id;
        this.name = name;
        this.website = website
    }

    /**
     * returns a HTML element with a link to the source and the source name as the text
     * @returns {JSX.Element}
     */
    asWebLink(): React.JSX.Element {
        return <a href={this.website}>{this.name}</a>;
    }

    /**
     * Find a food source by name
     * @param name {string} the name of the food source
     * @returns {FoodSource} the food source
     */
    static findByName(name: string): FoodSource | undefined {
        return FoodSource.ENUM_VALUES.find(fs => fs.name === name);
    }
}

export class FoodType {
    /**
     * @type {[FoodType]}
     */
    static ENUM_VALUES: FoodType[] = [];

    static COMBINED_INGREDIENT = new FoodType(1, "COMBINED_INGREDIENT");
    static INGREDIENT = new FoodType(2, "INGREDIENT");
    static MEAL = new FoodType(3, "MEAL");
    static COMPOSITE_INGREDIENT = new FoodType(4, "COMPOSITE_INGREDIENT");

    id: number;
    name: string;

    /**
     * Represents a food type
     * @param id {number} the ID of the food type
     * @param name {string} the name of the food type
     */
    constructor(id: number, name: string) {
        this.id = id;
        this.name = name;
        FoodType.ENUM_VALUES.push(this);
    }
}

/**
 * Convert a food source ID into an object
 * @param id the id of the food source
 * @returns {FoodSource} the food source instance
 */
export function foodSrcId(id: number): FoodSource {
    return FoodSource.ENUM_VALUES[id] ?? FoodSource.ENUM_VALUES[0]!;
}

/**
 * Convert a food type into an object form
 * @param name {string} the food type name
 * @returns {FoodType} the instance
 */
export function foodTypeFromName(name: string): FoodType {
    return FoodType.ENUM_VALUES.find(ft => name === ft.name) ?? FoodType.ENUM_VALUES[0]!;
}

export class PortionSize {
    static ONE_GRAM = new PortionSize(-1, -1, "gram", "", 1)

    foodEntryId: number;
    portionSizeId: number;
    portionSizeName: string;
    knownName: string;
    gramQuantity: number;

    /**
     * A portion size that is part of a food entry
     * @param foodEntryId {number} the food entry ID
     * @param portionSizeId {number} the portion size ID
     * @param portionSizeName {string} the name of the portion
     * @param knownName {string} the known conversion name
     * @param gramQuantity {number} the gram quantity equivalent
     */
    constructor(foodEntryId: number, portionSizeId: number, portionSizeName: string, knownName: string, gramQuantity: number) {
        this.foodEntryId = foodEntryId;
        this.portionSizeId = portionSizeId;
        this.portionSizeName = portionSizeName;
        this.knownName = knownName;
        this.gramQuantity = gramQuantity;
    }

    /**
     * convert JSON data into a portion size
     * @param jsonData {any} the json data
     * @returns {PortionSize} the portion size
     */
    static fromJson(jsonData: any): PortionSize {
        return new PortionSize(
            jsonData.foodEntryId, jsonData.portionId, jsonData.portionName,
            jsonData.knownConversionName, jsonData.gramEquivalent);
    }

    /**
     * Convert to JSON
     * @returns {{foodEntryId, portionId, portionName, knownConversionName, gramEquivalent}}
     */
    toJson() {
        return {
            foodEntryId: this.foodEntryId,
            portionId: this.portionSizeId < 0 ? 0 : this.portionSizeId,
            portionName: this.portionSizeName,
            knownConversionName: this.knownName,
            gramEquivalent: this.gramQuantity
        }
    }
}

export class NutrientGroup {
    static CARB_ENERGY = new NutrientGroup("CARB_ENERGY");
    static FATS = new NutrientGroup("FATS");
    static VITAMINS = new NutrientGroup("VITAMINS");

    name: string;

    /**
     * Construct a nutrient group enumeration
     * @param name {string} the group name
     */
    constructor(name: string) {
        this.name = name;
    }
}

export class UnitType {
    unitType: string;

    constructor(unitType: string) {
        this.unitType = unitType;
    }

    static WEIGHT = new UnitType('Weight');
    static ENERGY = new UnitType('Energy');
}
export class Measurement {
    name: string;
    unit: string;
    factor: number;
    unitType: UnitType;

    /**
     * Creates a measurement object
     * @param name {string} the name
     * @param unit {string} the unit
     * @param factor {number} the factor
     * @param unitType {UnitType} the unit type
     */
    constructor(name: string, unit: string, factor: number, unitType: UnitType) {
        this.name = name;
        this.unit = unit;
        this.factor = factor;
        this.unitType = unitType;
        Measurement.ENUM_VALUES.push(this);
    }

    /**
     * the list of all measurements
     * @type {[Measurement]}
     */
    static ENUM_VALUES: Measurement[] = [];
    static KILOGRAM = new Measurement('KILOGRAM', 'kg', 1000.0, UnitType.WEIGHT);
    static GRAM = new Measurement('GRAM', 'g', 1.0, UnitType.WEIGHT);
    static MICROGRAM = new Measurement('MICROGRAM', 'µg', 0.000001, UnitType.WEIGHT);
    static MILLIGRAM = new Measurement('MILLIGRAM', 'mg', 0.001, UnitType.WEIGHT);
    static KILO_JOULE = new Measurement('KILO_JOULE', 'kJ', 1/4.184, UnitType.ENERGY);
    static KCAL = new Measurement('KCAL', 'kcal', 1.0, UnitType.ENERGY);

    /**
     * return the measurement for the string representation
     * @param str {string} the string representation either unit name or enum name
     * @return {Measurement} the measure
     */
    static fromString(str: string): Measurement {
        return Measurement.ENUM_VALUES.find(m => m.name === str || m.unit === str) ?? Measurement.GRAM;
    }

    /**
     * Checks if another measurement is compatible with this one
     * @param other {Measurement} the other measurement
     * @returns {boolean} true if compatible
     */
    isCompatibleWith(other: Measurement): boolean {
        return this.unitType === other.unitType;
    }

    /**
     * Converts the given value to the main unit using the factor.
     * @param {number} value - The numeric value to be converted.
     * @return {number} The value converted to the main unit.
     */
    toMainUnit(value: number): number {
        return value * this.factor;
    }

    /**
     * Converts a given amount from a source unit to the unit of the current instance.
     *
     * @param {number} amount - The numeric value to be converted.
     * @param {Measurement} measure - The source unit containing its conversion factor and compatibility checking method.
     * @return {number} The converted amount in the unit of the current instance.
     */
    fromSourceUnit(amount: number, measure: Measurement): number {
        if(!measure.isCompatibleWith(this)) {
            throw new Error("Measurements are not compatible");
        }
        return amount * measure.factor / this.factor;
    }
}

export class Nutrient {
    ordinal: number;
    name: string;
    group: NutrientGroup;
    defMeasure: Measurement;

    /**
     * Construct a nutrient from it's name, group and default measurement
     * @param name {string} the name
     * @param group {NutrientGroup} the nutrient group
     * @param defMeasure {Measurement} the default measurement
     * @param last {boolean} true if last in the list of nutrients
     */
    constructor(name: string, group: NutrientGroup, defMeasure: Measurement, last: boolean = false) {
        this.ordinal = Nutrient.ENUM_VALUES.length + 1;
        this.name = name;
        this.group = group;
        this.defMeasure = defMeasure;
        Nutrient.ENUM_VALUES.push(this);
        if(last) Object.freeze(Nutrient.ENUM_VALUES);
    }

    /**
     * The list of Nutrient enumerations
     * @type {[Nutrient]}
     */
    static ENUM_VALUES: Nutrient[] = [];
    static ENERGY = new Nutrient('ENERGY', NutrientGroup.CARB_ENERGY, Measurement.KILO_JOULE);
    static CALORIES = new Nutrient('CALORIES', NutrientGroup.CARB_ENERGY, Measurement.KCAL);
    static SODIUM = new Nutrient("SODIUM", NutrientGroup.CARB_ENERGY, Measurement.MILLIGRAM);
    static FIBRE = new Nutrient("FIBRE", NutrientGroup.CARB_ENERGY, Measurement.GRAM);
    static PROTEIN = new Nutrient("PROTEIN", NutrientGroup.CARB_ENERGY, Measurement.GRAM);
    static CARBOHYDRATE = new Nutrient("CARBOHYDRATE", NutrientGroup.CARB_ENERGY, Measurement.GRAM);
    static SUGAR = new Nutrient("SUGAR", NutrientGroup.CARB_ENERGY, Measurement.GRAM);
    static TOTAL_FAT = new Nutrient('TOTAL_FAT', NutrientGroup.FATS, Measurement.GRAM);
    static SATURATED_FAT = new Nutrient("SATURATED_FAT", NutrientGroup.FATS, Measurement.GRAM);
    static POLYUNSATURATED_FAT = new Nutrient("POLYUNSATURATED_FAT", NutrientGroup.FATS, Measurement.GRAM);
    static MONOUNSATURATED_FAT = new Nutrient("MONOUNSATURATED_FAT", NutrientGroup.FATS, Measurement.GRAM);
    static CHOLESTEROL = new Nutrient("CHOLESTEROL", NutrientGroup.FATS, Measurement.MILLIGRAM);
    static TRANS_FAT = new Nutrient("TRANS_FAT", NutrientGroup.FATS, Measurement.GRAM);
    static VITAMIN_A = new Nutrient("VITAMIN_A", NutrientGroup.VITAMINS, Measurement.MICROGRAM);
    static VITAMIN_B = new Nutrient("VITAMIN_B", NutrientGroup.VITAMINS, Measurement.MILLIGRAM);
    static VITAMIN_C = new Nutrient("VITAMIN_C", NutrientGroup.VITAMINS, Measurement.MILLIGRAM);
    static VITAMIN_D = new Nutrient("VITAMIN_D", NutrientGroup.VITAMINS, Measurement.MICROGRAM);
    static VITAMIN_E = new Nutrient("VITAMIN_E", NutrientGroup.VITAMINS, Measurement.MILLIGRAM);
    static VITAMIN_K = new Nutrient("VITAMIN_K", NutrientGroup.VITAMINS, Measurement.MILLIGRAM);
    static CALCIUM = new Nutrient("CALCIUM", NutrientGroup.VITAMINS, Measurement.MILLIGRAM);
    static IRON = new Nutrient("IRON", NutrientGroup.VITAMINS, Measurement.MICROGRAM);
    static MAGNESIUM = new Nutrient("MAGNESIUM", NutrientGroup.VITAMINS, Measurement.MILLIGRAM);
    static POTASSIUM = new Nutrient("POTASSIUM", NutrientGroup.VITAMINS, Measurement.MILLIGRAM);
    static SELENIUM = new Nutrient("SELENIUM", NutrientGroup.VITAMINS, Measurement.MICROGRAM);
    static ZINC = new Nutrient("ZINC", NutrientGroup.VITAMINS, Measurement.MILLIGRAM, true);

    /**
     * return a nutrient from a string
     * @param nutrient {string} the text representation
     * @returns {Nutrient | undefined} the object form
     */
    static fromString(nutrient: string): Nutrient | undefined {
        return Nutrient.ENUM_VALUES.find(en => en.name === nutrient)
    }
}

export class FoodNutrient {
    foodEntryId: number;
    foodNutrientId: number;
    nutrient: Nutrient;
    measurement: Measurement;
    value: number;

    /**
     * Create a food nutrient entry
     * @param foodEntryId {number} the food entry ID
     * @param foodNutrientId {number} the food nutrient ID
     * @param nutrient {Nutrient} the nutrient
     * @param measurement {Measurement} the measurement for the value
     * @param value {number} the quantity in the given measurement
     */
    constructor(foodEntryId: number, foodNutrientId: number, nutrient: Nutrient, measurement: Measurement, value: number) {
        this.foodEntryId = foodEntryId;
        this.foodNutrientId = foodNutrientId;
        this.nutrient = nutrient;
        this.measurement = measurement;
        this.value = value;
    }

    /**
     * Convert nutrient JSON into an instance
     * @param jsonData {any} the parsed json data
     * @returns {FoodNutrient} the food nutrient object
     */
    static fromJson(jsonData: any): FoodNutrient {
        const nutrient = Nutrient.fromString(jsonData.nutrient);
        if (!nutrient) throw new Error(`Unknown nutrient: ${jsonData.nutrient}`);
        return new FoodNutrient(
            jsonData.foodEntryId, jsonData.foodNutrientId, nutrient,
            Measurement.fromString(jsonData.measurement), jsonData.quantity);
    }

    /**
     * The JSON object representation
     * @returns {Object} JSON object
     */
    toJson(): any {
        return {
            foodEntryId: this.foodEntryId,
            foodNutrientId: this.foodNutrientId < 0 ? 0 : this.foodNutrientId,
            nutrient: this.nutrient.name,
            measurement: this.measurement.name,
            quantity: this.value
        }
    }
}

export class FoodEntry {
    static EMPTY = new FoodEntry(0, "", "", FoodSource.ENUM_VALUES[0]!, FoodType.INGREDIENT, "", new Date(), new Date(), "WebApp", 0,[], []);

    id: number;
    name: string;
    category: string;
    foodSource: FoodSource;
    foodType: FoodType;
    externalId: string;
    publicationDate: Date;
    lastUpdated: Date;
    userName: string;
    version: number;
    portions: PortionSize[];
    nutrients: FoodNutrient[];

    /**
     *
     * @param id {number} the database identifier for the food entry
     * @param name {string} the name of the food entry
     * @param category {string} the name of the category
     * @param foodSource {FoodSource} the source for the food
     * @param foodType {FoodType} the type for the food
     * @param externalId {string} the external ID of the food
     * @param publicationDate {Date} the publication date
     * @param lastUpdated {Date} last update time
     * @param userName {string} the user name for the update
     * @param version {number} the version of the food entry
     * @param portions {[PortionSize]} an array of portion sizes
     * @param nutrients {[FoodNutrient]} an array of food nutrients
     */
    constructor(id: number, name: string, category: string, foodSource: FoodSource, foodType: FoodType, externalId: string,
                publicationDate: Date, lastUpdated: Date, userName: string, version: number, portions: PortionSize[], nutrients: FoodNutrient[]) {
        this.id = id;
        this.name = name;
        this.category = category;
        this.foodSource = foodSource;
        this.foodType = foodType;
        this.externalId = externalId;
        this.publicationDate = publicationDate;
        this.lastUpdated = lastUpdated;
        this.userName = userName;
        this.version = version;
        this.portions = portions;
        this.nutrients = nutrients;

    }

    /**
     * Convert JSON data into an instance of FoodEntry
     * @param jsonData {any} the Json data
     * @return {FoodEntry} the created entity
     */
    static fromJson(jsonData: any): FoodEntry {
        let portions = jsonData.portionSizes.map((ps: any) => PortionSize.fromJson(ps));
        let nutrients = jsonData.nutrients.map((nu: any) => FoodNutrient.fromJson(nu));
        return new FoodEntry(
            jsonData.foodEntryId, jsonData.name, jsonData.category, foodSrcId(jsonData.foodSource.id),
            foodTypeFromName(jsonData.foodType), jsonData.externalId, new Date(jsonData.publicationDate),
            new Date(jsonData.lastUpdate), jsonData.userName ?? "Unknown", jsonData.version ?? 0,
            portions, nutrients
        );
    }

    toJson(): any {
        return {
            foodEntryId: this.id,
            name: this.name,
            category: this.category,
            foodSource: {
                id: this.foodSource.id,
                name: this.foodSource.name
            },
            foodType: this.foodType.name,
            externalId: this.externalId,
            publicationDate: this.publicationDate.getFullYear() + "-" + String(this.publicationDate.getMonth() + 1).padStart(2, '0') + "-" + String(this.publicationDate.getUTCDate()).padStart(2, '0'),
            lastUpdate: this.lastUpdated.toISOString(),
            userName: this.userName,
            version: this.version,
            portionSizes: this.portions.map(ps => ps.toJson()),
            nutrients: this.nutrients.map(nu => nu.toJson())
        }
    }

    /**
     * Get the nutrients in order of their ordinal
     * @returns {FoodNutrient[]} the list of nutrients
     */
    nutrientsInOrder(): FoodNutrient[] {
        return this.nutrients.sort((a, b) => a.nutrient.ordinal < b.nutrient.ordinal ? -1 : 1);
    }

    /**
     * Get the portion size for the given portion ID or one gram otherwise
     * @param intId {number} the portion ID
     * @returns {PortionSize} the portion size
     */
    portionFor(intId: number): PortionSize {
        let ps = this.portions.find(ps => ps.portionSizeId === intId);
        if(ps) return ps;
        return PortionSize.ONE_GRAM;
    }
}